import './xy-ui/xy-select.js';
import './xy-ui/xy-tips.js';
import { type WormholeList } from './service';
import {
  createInnerElement,
  createDragIcon,
  createSelectElement,
  createOuterElement,
} from './utils/create-select';
import { inIframe } from './utils';

interface OnValueChange {
  (value: string): void;
}

class MySelect {
  private selectElement!: HTMLElement;

  private style: Partial<CSSStyleDeclaration>;

  private options: WormholeList[];

  private selectedValue: string;

  private onValueChange: OnValueChange;

  private selectDisable = false;

  constructor(
    style = {},
    options: WormholeList[],
    selectedValue: string,
    onValueChange: OnValueChange,
    selectDisable?: boolean,
  ) {
    this.options = options;
    this.style = style;
    this.selectedValue = selectedValue;
    this.onValueChange = onValueChange;
    this.selectDisable = selectDisable ?? false;
  }

  static enableDrag(element: HTMLElement, icon: HTMLElement) {
    let isMouseDown = false;
    let needRAF = true;
    let mousedownX = 0;
    let mousedownY = 0;
    const transform = {
      translateX: 0,
      translateY: 0,
    };
    let initTranslateX = transform.translateX;
    let initTranslateY = transform.translateY;
    let currentTranslateX = transform.translateX;
    let currentTranslateY = transform.translateY;
    const maxTranslateX = 0;
    let minTranslateX = 0;
    let maxTranslateY = 0;
    const minTranslateY = 0;

    const elementStyle = element.style;
    elementStyle.transform = `translate(${initTranslateX}px, ${initTranslateY}px)`;

    icon.addEventListener('mousedown', e => {
      isMouseDown = true;
      mousedownX = e.pageX;
      mousedownY = e.pageY;
      minTranslateX = -(window.innerWidth - element.clientWidth);
      maxTranslateY = window.innerHeight - element.clientHeight;
    });

    document.addEventListener('mouseup', () => {
      if (isMouseDown) {
        isMouseDown = false;
        initTranslateX = currentTranslateX;
        initTranslateY = currentTranslateY;
      }
    });

    document.addEventListener('mousemove', e => {
      if (isMouseDown && needRAF) {
        needRAF = false;
        requestAnimationFrame(() => {
          needRAF = true;
          let translateX = initTranslateX + e.pageX - mousedownX;
          let translateY = initTranslateY + e.pageY - mousedownY;
          if (translateX > maxTranslateX) {
            translateX = maxTranslateX;
          }
          if (translateX < minTranslateX) {
            translateX = minTranslateX;
          }
          if (translateY > maxTranslateY) {
            translateY = maxTranslateY;
          }
          if (translateY < minTranslateY) {
            translateY = minTranslateY;
          }
          currentTranslateX = translateX;
          currentTranslateY = translateY;
          elementStyle.transform = `translate(${translateX}px, ${translateY}px)`;
        });
      }
    });
  }

  public renderSelect() {
    if (inIframe()) return;
    const selectElement = createSelectElement(this.selectedValue, this.options);
    this.selectElement = selectElement;
    selectElement.addEventListener('change', e => {
      const selectedValue = (e.target as HTMLSelectElement).value;
      this.onValueChange(selectedValue);
    });

    const innerWrapper = createInnerElement();
    const dragIcon = createDragIcon();

    innerWrapper.appendChild(selectElement);
    innerWrapper.appendChild(dragIcon);

    const style: Partial<CSSStyleDeclaration> = this.style ?? {};
    const outerWrapper = createOuterElement(style);

    MySelect.enableDrag(outerWrapper, dragIcon);
    outerWrapper.appendChild(innerWrapper);
    document.body.appendChild(outerWrapper);
    if (this.selectDisable) {
      selectElement.setAttribute('disabled', 'disabled');
    }
  }

  public disableSelect() {
    const { selectElement } = this;
    if (selectElement) {
      selectElement.setAttribute('disabled', 'disabled');
    }
    this.selectDisable = true;
  }

  public enableSelect() {
    const { selectElement } = this;
    if (selectElement) {
      selectElement.removeAttribute('disabled');
    }
    this.selectDisable = false;
  }
}

export default MySelect;
